import React, { useEffect, useRef, useState } from 'react';
import { connect, useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';

import BasePrompt from 'components/base/prompt/BasePrompt';
import CustomerFilterableDropdown from 'components/transactions/CustomerFilterableDropdown';
import CustomTitleWithSwitch from 'components/base/CustomTitleWithSwitch';
import { CustomTitleLabel, CustomTitleWithRadio } from 'components/earning/CustomBaseComponments';
import { CustomStatusTagFromText } from 'components/base/CustomStatusTag';
import CustomTitleWithInput from 'components/base/CustomTitleWithInput';
import './MergeCustomerPrompt.scss';
import Loading from 'components/base/Loading';
import { MEMBERSHIP_PERIOD_MODE, PointSettingExpiryRule } from 'config/CustomEnums';

const CustomerTransferActionType = {
  TRANSFER_TO_PRIMARY_ACCOUNT: "TRANSFER_TO_PRIMARY_ACCOUNT",
  TRANSFER_CUSTOM_AMOUNT: "TRANSFER_CUSTOM_AMOUNT",
  NO_ACTION: "NO_ACTION",
};

const checkShouldAddAsSecondary = (
  unwantedAccount,
  primaryAccount,
) => {
  if (
    primaryAccount?.mobilePhoneNumberCountryCode
    && primaryAccount?.mobilePhoneNumberSubscriberNumber
    && primaryAccount?.emailAddress
  ) {
    return {
      checkStatus: false,
      checkMethod: "",
    };
  };
  if (
    primaryAccount?.mobilePhoneNumberCountryCode
    && primaryAccount?.mobilePhoneNumberSubscriberNumber
    && unwantedAccount?.emailAddress
  ) {
    return {
      checkStatus: true,
      checkMethod: `email (${unwantedAccount?.emailAddress})`,
    };
  };
  if (
    primaryAccount?.emailAddress
    && unwantedAccount?.mobilePhoneNumberCountryCode
    && unwantedAccount?.mobilePhoneNumberSubscriberNumber
  ) {
    return {
      checkStatus: true,
      checkMethod: `mobile (+${unwantedAccount?.mobilePhoneNumberCountryCode
        } ${unwantedAccount?.mobilePhoneNumberSubscriberNumber
        })`,
    }
  }
  return {
    checkStatus: false,
    checkMethod: null,
  };
};

const accountFieldsList = [
  {
    title: "Name",
    valueField: "owner",
  },
  {
    title: "Mobile",
    valueField: "mobileNumber",
  },
  {
    title: "Email",
    valueField: "email",
  },
  {
    title: "Membership ID",
    valueField: "membershipId",
  },
  {
    title: "Membership level",
    valueField: "level",
  },
];

const AccountField = ({
  title,
  value,
}) => (
  <div className='merge-customer-single-field-container'>
    <span>{title}</span>
    <span>{": "}</span>
    <span>{value || "-"}</span>
  </div>
);

const AccountStatusField = ({
  value,
}) => (
  <div className='merge-customer-single-field-container'>
    <span>{"Account status: "}</span>
    {CustomStatusTagFromText(value, 'customer-detail-status')}
  </div>
);

const AccountMergeHistoryField = ({
  value,
}) => (
  <div className='merge-customer-single-field-container'>
    <span>{"Account merge history: "}</span>
    <label className={`custom-tag-label ${value
      ? 'custom-tag-label-publish'
      : 'custom-tag-label-expired'
      } customer-detail-status`}
    >
      {value ? 'Yes' : 'No'}
    </label>
  </div>
)

const SeparateLine = () => (
  <div className="merge-customer-separate-line" />
);

const HandlingField = ({
  title,
  tips,
  value,
  setValue,
  transferAllTips,
  haveTransferCustomOption,
  transferCustomTips,
}) => {
  const options = [
    {
      label: "Transfer to primary account",
      value: CustomerTransferActionType.TRANSFER_TO_PRIMARY_ACCOUNT,
      popoverTips: transferAllTips,
    },
    ...(haveTransferCustomOption
      ? [
        {
          label: "Transfer custom amount",
          value: CustomerTransferActionType.TRANSFER_CUSTOM_AMOUNT,
          popoverTips: transferCustomTips,
        }
      ]
      : []
    ),
    {
      label: "No action",
      value: CustomerTransferActionType.NO_ACTION,
    },
  ]

  return (
    <CustomTitleWithRadio
      title={title}
      tips={tips}
      options={options}
      defaultValue={value}
      setValue={setValue}
    />
  )
}

const validate = (
  data,
  maxPoints,
  showPointExpiryDays,
  maxPointExpiryDays,
  maxProgressionPoint,
  showLevelExpiryDays,
  maxLevelExpiryDays,
) => {
  const errors = [];
  if (data.handlingPointType === CustomerTransferActionType.TRANSFER_CUSTOM_AMOUNT) {
    if (maxPoints < 0) {
      if (data.transferPointAmount >= 0) {
        errors.push('transferPointAmountInvalid');
      } else if (data.transferPointAmount < maxPoints) {
        errors.push('transferPointAmount');
      }
    } else {
      if (data.transferPointAmount <= 0) {
        errors.push('transferPointAmountInvalid');
      }
      if (data.transferPointAmount > maxPoints) {
        errors.push('transferPointAmount');
      }
    }

    if (data.transferPointAmount > maxPoints) {
      errors.push('transferPointAmount');
    };
    if (showPointExpiryDays && data.pointExpiryDays > maxPointExpiryDays) {
      errors.push('pointExpiryDays');
    };
  };
  if (data.handlingProgressionPointType === CustomerTransferActionType.TRANSFER_CUSTOM_AMOUNT) {
    if (data.transferProgressionPointAmount > maxProgressionPoint) {
      errors.push('transferProgressionPointAmount');
    };
    if (showLevelExpiryDays && data.levelExpiryDays > maxLevelExpiryDays) {
      errors.push('levelExpiryDays');
    };
  };
  return errors;
};

const MergeCustomerPrompt = ({
  show,
  setShow,
  unwantedAccount,
  primaryAccount,
  primaryAccountLoading,
  pointGlobalSetting,
  levelGlobalSetting,
}) => {
  const history = useHistory();
  const dispatch = useDispatch();

  const haveClickedRef = useRef();

  const showPointExpiryDays = pointGlobalSetting?.expiryRule === PointSettingExpiryRule.EXPIRE_AFTER_X_DAYS;
  const showLevelExpiryDays = levelGlobalSetting?.membershipPeriodMode === MEMBERSHIP_PERIOD_MODE.dynamic;
  const pointsBalance = unwantedAccount?.pointsBalance || 0;
  const pointsValidityPeriod = pointGlobalSetting?.pointsValidityPeriod || 365;
  const tpe = unwantedAccount?.tpe || 0;
  const dynamicDuration = levelGlobalSetting?.dynamicDuration || 365;

  const { checkStatus, checkMethod } = checkShouldAddAsSecondary(unwantedAccount, primaryAccount);

  const [selectedCustomer, setSelectedCustomer] = useState();
  const [handlingTransactionType, setHandlingTransactionType] = useState(CustomerTransferActionType.TRANSFER_TO_PRIMARY_ACCOUNT);
  const [handlingCouponType, setHandlingCouponType] = useState(CustomerTransferActionType.TRANSFER_TO_PRIMARY_ACCOUNT);
  const [handlingTicketType, setHandlingTicketType] = useState(CustomerTransferActionType.TRANSFER_TO_PRIMARY_ACCOUNT);
  const [handlingStampType, setHandlingStampType] = useState(CustomerTransferActionType.TRANSFER_TO_PRIMARY_ACCOUNT);
  const [handlingPointType, setHandlingPointType] = useState(CustomerTransferActionType.TRANSFER_TO_PRIMARY_ACCOUNT);
  const [transferPointAmount, setTransferPointAmount] = useState(pointsBalance);
  const [pointExpiryDays, setPointExpiryDays] = useState(pointsValidityPeriod);
  const [handlingProgressionPointType, setHandlingProgressionPointType] = useState(CustomerTransferActionType.TRANSFER_TO_PRIMARY_ACCOUNT);
  const [transferProgressionPointAmount, setTransferProgressionPointAmount] = useState(tpe);
  const [levelExpiryDays, setLevelExpiryDays] = useState(dynamicDuration);
  const [shouldAddAsSecondary, setShouldAddAsSecondary] = useState(false);
  const [haveReadReminder, setHaveReadReminder] = useState(false);
  const [errors, setErrors] = useState([]);

  const resetPrompt = () => {
    setHandlingTransactionType(CustomerTransferActionType.TRANSFER_TO_PRIMARY_ACCOUNT);
    setHandlingCouponType(CustomerTransferActionType.TRANSFER_TO_PRIMARY_ACCOUNT);
    setHandlingTicketType(CustomerTransferActionType.TRANSFER_TO_PRIMARY_ACCOUNT);
    setHandlingStampType(CustomerTransferActionType.TRANSFER_TO_PRIMARY_ACCOUNT);
    setHandlingPointType(CustomerTransferActionType.TRANSFER_TO_PRIMARY_ACCOUNT);
    setTransferPointAmount(pointsBalance);
    setPointExpiryDays(pointsValidityPeriod);
    setHandlingProgressionPointType(CustomerTransferActionType.TRANSFER_TO_PRIMARY_ACCOUNT);
    setTransferProgressionPointAmount(tpe);
    setLevelExpiryDays(dynamicDuration);
    setShouldAddAsSecondary(false);
    setHaveReadReminder(false);
    setErrors([]);
  };

  const onClose = () => {
    setSelectedCustomer();
    resetPrompt();
    setShow(false);
  };

  useEffect(() => {
    dispatch({ type: 'pointTransactionList/getPointGlobalSetting' });
    dispatch({ type: 'levels/getLevelGlobalSetting' });
  }, [])

  useEffect(() => {
    if (selectedCustomer) {
      resetPrompt();
    }
  }, [selectedCustomer])

  const handlingFieldsList = [
    {
      title: "Handling of transaction records",
      value: handlingTransactionType,
      setValue: setHandlingTransactionType,
      transferAllTips: (
        <div>
          <ul>
            <li>
              Customer & admin will be able to view the transaction details in the primary account;
            </li>
            <li>
              Earning & reward limits will count the transferred transactions when create new transactions.
            </li>
          </ul>
        </div>
      ),
      haveTransferCustomOption: false,
    },
    {
      title: "Handling of coupons",
      value: handlingCouponType,
      setValue: setHandlingCouponType,
      transferAllTips: (
        <div>
          <ul>
            <li>
              The primary account will become the coupon owner;
            </li>
            <li>
              Coupon expiry date will remain the same;
            </li>
            <li>
              New coupon records & activity logs will be generated;
            </li>
            <li>
              Coupon acquiring, earning & reward limits will be ignored when transfer coupons;
            </li>
            <li>
              Coupon acquiring / earning & reward limits will count the transferred coupons when acquire / earn new coupons.
            </li>
          </ul>
        </div>
      ),
      haveTransferCustomOption: false,
    },
    {
      title: "Handling of tickets",
      value: handlingTicketType,
      setValue: setHandlingTicketType,
      transferAllTips: (
        <div>
          <ul>
            <li>
              The primary account will become the ticket owner;
            </li>
            <li>
              Ticket expiry date will remain the same;
            </li>
            <li>
              New ticket records & activity logs will be generated;
            </li>
            <li>
              Ticket acquiring, earning & reward limits will be ignored when transfer tickets;
            </li>
            <li>
              Ticket acquiring / earning & reward limits will count the transferred tickets when acquire / earn new tickets.
            </li>
          </ul>
        </div>
      ),
      haveTransferCustomOption: false,
    },
    {
      title: "Handling of stamps",
      value: handlingStampType,
      setValue: setHandlingStampType,
      transferAllTips: (
        <div>
          <ul>
            <li>
              New stamp records & activity logs will be generated;
            </li>
            <li>
              Earning & reward limits will be ignored when transfer stamps;
            </li>
            <li>
              Earning & reward limits will count the transferred stamps when earn new stamps.
            </li>
          </ul>
        </div>
      ),
      haveTransferCustomOption: false,
    },
  ];

  const handlingPointsField = {
    title: "Handling of points (for coupon redemption & cash dollar)",
    value: handlingPointType,
    setValue: setHandlingPointType,
    transferAllTips: (
      <div>
        <ul>
          <li>
            All unused & unexpired points will be transferred to the primary account;
          </li>
          <li>
            Point expiry date will remain the same;
          </li>
          <li>
            New point records & activity logs will be generated;
          </li>
          <li>
            Earning & reward limits will be ignored when transfer points;
          </li>
          <li>
            Earning & reward limits will count the transferred points when earn new points.
          </li>
        </ul>
      </div>
    ),
    haveTransferCustomOption: true,
    transferCustomTips: (
      <div>
        <ul>
          <li>
            The custom amount cannot exceed the point balance;
          </li>
          <li>
            One new point record & activity log will be generated;
          </li>
          <li>
            Earning & reward limits will be ignored when transfer points;
          </li>
          <li>
            Earning & reward limits will count the transferred points when earn new points.
          </li>
        </ul>
      </div>
    )
  };

  const handlingProgressionPointField = {
    title: "Handling of progression points (PP) (for level upgrade & renewal)",
    tips: "The transfer of PP may trigger level upgrade.",
    value: handlingProgressionPointType,
    setValue: setHandlingProgressionPointType,
    transferAllTips: (
      <div>
        <ul>
          <li>
            Only PP earned in current membership period will be transferred;
          </li>
          <li>
            One new point record & activity log will be generated;
          </li>
          <li>
            Earning & reward limits will be ignored when transfer PP;
          </li>
          <li>
            Earning & reward limits will count the transferred PP when earn new PP.
          </li>
        </ul>
      </div>
    ),
    haveTransferCustomOption: true,
    transferCustomTips: (
      <div>
        <ul>
          <li>
            The custom amount cannot exceed the accumulated PP earned in current membership period;
          </li>
          <li>
            One new point record & activity log will be generated;
          </li>
          <li>
            Earning & reward limits will be ignored when transfer PP;
          </li>
          <li>
            Earning & reward limits will count the transferred PP when earn new PP.
          </li>
        </ul>
      </div>
    )
  };

  const otherBody = () => {
    return (
      <>
        <CustomTitleLabel title="Current account (the unwanted account)" />
        <div className='merge-customer-field-container'>
          {
            accountFieldsList.map(item => (
              <AccountField
                key={`unwanted-account-${item.title}`}
                title={item.title}
                value={unwantedAccount?.[item.valueField]}
              />
            ))
          }
          <AccountStatusField value={unwantedAccount?.status} />
          <AccountMergeHistoryField
            value={unwantedAccount?.haveTransferHistory}
          />
        </div>
        <SeparateLine />

        <CustomerFilterableDropdown
          title={'Select the primary account (the account to be kept)'}
          defaultSelectedCustomer={
            selectedCustomer?.pk
              ? {
                label: selectedCustomer?.owner || "null null",
                value: selectedCustomer,
              }
              : null
          }
          selectCustomer={(data) => {
            setSelectedCustomer(data?.value);
            if (data?.value?.pk) {
              dispatch({
                type: 'customerList/getPrimaryAccount',
                payload: { id: data?.value?.pk }
              });
            }
          }}
          moreSearch={{ isDropdownNode: true }}
        />

        {primaryAccountLoading && <Loading />}
        {
          selectedCustomer && !primaryAccountLoading && (
            <>
              <div className='merge-customer-field-container'>
                {
                  accountFieldsList.map(item => (
                    <AccountField
                      key={`primary-account-${item.title}`}
                      title={item.title}
                      value={primaryAccount?.[item.valueField]}
                    />
                  ))
                }
                <AccountStatusField value={primaryAccount?.status} />
                <AccountMergeHistoryField
                  value={primaryAccount?.haveTransferHistory}
                />
              </div>

              <div className='merge-customer-handling-field-container'>
                {
                  handlingFieldsList.map(item => (
                    <div key={item.title}>
                      <HandlingField
                        {...item}
                      />
                      <SeparateLine />
                    </div>
                  ))
                }
                <HandlingField
                  {...handlingPointsField}
                />
                {
                  handlingPointType === CustomerTransferActionType.TRANSFER_CUSTOM_AMOUNT
                  && (
                    <>
                      <CustomTitleWithInput
                        type='number'
                        supportNegative
                        title="Transfer amount"
                        tips={`Point balance: ${pointsBalance}`}
                        defaultValue={transferPointAmount}
                        setValue={(value) => {
                          setTransferPointAmount(value);
                        }}
                        customLineClass="merge-customer-amount-field"
                        unit="points"
                        error={{
                          id: 'transferPointAmount',
                          error: errors.find(item => [
                            "transferPointAmount",
                            "transferPointAmountInvalid"
                            ].includes(item)
                          ),
                          message: errors.find(item => item === "transferPointAmount")
                            ? 'The custom amount cannot exceed the point balance.'
                            : 'Invalid value',
                        }}
                      />
                      {
                        showPointExpiryDays && (
                          <CustomTitleWithInput
                            type='number'
                            title="Point expiry date"
                            defaultValue={pointExpiryDays}
                            setValue={(value) => {
                              setPointExpiryDays(value);
                            }}
                            customLineClass="merge-customer-amount-field"
                            per="After"
                            unit="days"
                            error={{
                              id: 'pointExpiryDays',
                              error: errors.find(item => item === 'pointExpiryDays'),
                              message: `Point expiry date cannot be longer than ${pointsValidityPeriod} days.`,
                            }}
                          />
                        )
                      }
                    </>
                  )
                }
                <SeparateLine />

                <HandlingField
                  {...handlingProgressionPointField}
                />
                {
                  handlingProgressionPointType === CustomerTransferActionType.TRANSFER_CUSTOM_AMOUNT
                  && (
                    <>
                      <CustomTitleWithInput
                        type='number'
                        title="Transfer amount"
                        tips={`Accumulated PP: ${tpe}`}
                        defaultValue={transferProgressionPointAmount}
                        setValue={(value) => {
                          setTransferProgressionPointAmount(value);
                        }}
                        customLineClass="merge-customer-amount-field"
                        unit="PP"
                        error={{
                          id: 'transferProgressionPointAmount',
                          error: errors.find(item => item === 'transferProgressionPointAmount'),
                          message: "The custom amount cannot exceed the accumulated PP earned in current membership period.",
                        }}
                      />
                      {
                        showLevelExpiryDays && (
                          <CustomTitleWithInput
                            type='number'
                            title="Level expiry date if upgrade is triggered"
                            defaultValue={levelExpiryDays}
                            setValue={(value) => {
                              setLevelExpiryDays(value);
                            }}
                            customLineClass="merge-customer-amount-field"
                            per="After"
                            unit="days"
                            error={{
                              id: 'levelExpiryDays',
                              error: errors.find(item => item === 'levelExpiryDays'),
                              message: `Level expiry date cannot be longer than ${dynamicDuration} days.`,
                            }}
                          />
                        )
                      }
                    </>
                  )
                }
                {
                  checkStatus && (
                    <>
                      <SeparateLine />
                      <CustomTitleWithSwitch
                        title={
                          `Add the ${checkMethod} of the unwanted account to the primary account as second login?`
                        }
                        customTitleClass='customer-detail-prompt-field-title'
                        defaultValue={shouldAddAsSecondary}
                        setValue={(value) => {
                          setShouldAddAsSecondary(value);
                        }}
                      />
                    </>
                  )
                }
              </div>

              <CustomTitleLabel title="Items to remind the customer:" />
              <div className='merge-customer-have-read-reminder-container'>
                <input
                  type="checkbox"
                  checked={haveReadReminder}
                  onChange={(e) => {
                    setHaveReadReminder(!haveReadReminder);
                  }}
                />
                <lable className='merge-customer-reminder'>
                  {"After account merging, the process cannot be reversed.\r\nOnly the primary account can be used.\r\nThe primary account will not be able to view the activity logs of the unused account."}
                </lable>
              </div>
            </>
          )
        }
      </>
    )
  }

  return (
    <BasePrompt
      customClass={'merge-customer-prompt-container'}
      show={show}
      closeAction={onClose}
      leftButton={{
        text: 'Cancel',
        action: onClose,
      }}
      rightButton={{
        text: 'Confirm',
        disabled: !selectedCustomer || !haveReadReminder,
        action: () => {
          if (haveClickedRef.current) {
            return;
          };
          haveClickedRef.current = true;

          const formData = {
            unwantedAccountId: unwantedAccount?.pk,
            primaryAccountId: primaryAccount?.pk,
            handlingTransactionType: handlingTransactionType,
            handlingCouponType: handlingCouponType,
            handlingTicketType: handlingTicketType,
            handlingStampType: handlingStampType,
            handlingPointType: handlingPointType,
            transferPointAmount: transferPointAmount || null,
            pointExpiryDays: pointExpiryDays || null,
            handlingProgressionPointType: handlingProgressionPointType,
            transferProgressionPointAmount: transferProgressionPointAmount || null,
            levelExpiryDays: levelExpiryDays || null,
            addAnotherSignUpMethod: shouldAddAsSecondary || false,
          };

          const validateErrors = validate(
            formData,
            pointsBalance,
            showPointExpiryDays,
            pointsValidityPeriod,
            tpe,
            showLevelExpiryDays,
            dynamicDuration,
          );
          setErrors(validateErrors);
          if (!validateErrors.length) {
            dispatch({
              type: 'customerList/transferCustomer',
              payload: {
                input: formData,
                afterAction: () => {
                  onClose();
                  history.push({ pathname: `/customers/${primaryAccount?.pk}/` });
                  haveClickedRef.current = false;
                },
                errorAction: () => {
                  haveClickedRef.current = false;
                }
              }
            })
          } else {
            haveClickedRef.current = false;
          }
        },
      }}
      title="Merge to primary account"
      tips="If a customer has multiple accounts, he can merge the unwanted accounts to a primary account that he wants to keep."
      otherBody={otherBody}
    />
  );
}

const mapStateToProps = (state) => ({
  unwantedAccount: state.customerList.customer,
  primaryAccount: state.customerList.primaryAccount,
  primaryAccountLoading: state.customerList.primaryAccountLoading,
  pointGlobalSetting: state.pointTransactionList.pointGlobalSetting,
  levelGlobalSetting: state.levels.levelGlobalSetting,
})

export default connect(mapStateToProps)(MergeCustomerPrompt);
